import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, Factory, Calendar, Package, User, 
  Loader2, AlertCircle, CheckCircle2, Clock, FileText, ExternalLink
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  created: { label: 'Created', bg: 'bg-blue-100', text: 'text-blue-800' },
  issued: { label: 'Issued', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  received: { label: 'Received', bg: 'bg-green-100', text: 'text-green-800' },
  closed: { label: 'Closed', bg: 'bg-gray-100', text: 'text-gray-800' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100', text: 'text-red-800' }
};

const PROCESS_STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'bg-gray-100', text: 'text-gray-700' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100', text: 'text-blue-700' },
  completed: { label: 'Completed', bg: 'bg-green-100', text: 'text-green-700' }
};

const WOViewOnlyPage = () => {
  const { woId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workOrder, setWorkOrder] = useState(null);
  const [error, setError] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchWorkOrder = useCallback(async () => {
    if (!woId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/work-orders/${woId}`, {
        headers: getAuthHeader()
      });
      setWorkOrder(response.data);
    } catch (err) {
      console.error('Error fetching work order:', err);
      setError('Failed to load work order details');
      toast.error('Failed to load work order');
    } finally {
      setLoading(false);
    }
  }, [woId]);

  useEffect(() => {
    fetchWorkOrder();
  }, [fetchWorkOrder]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getProcessStatus = (process) => {
    if (!process.jobs || process.jobs.length === 0) return 'pending';
    const totalIssued = process.jobs.reduce((sum, j) => sum + (j.issued_qty || 0), 0);
    const totalReceived = process.jobs.reduce((sum, j) => sum + (j.received_qty || 0), 0);
    if (totalReceived >= process.quantity) return 'completed';
    if (totalIssued > 0) return 'in_progress';
    return 'pending';
  };

  const getProcessProgress = (process) => {
    if (!process.jobs || process.jobs.length === 0) return 0;
    const totalReceived = process.jobs.reduce((sum, j) => sum + (j.received_qty || 0), 0);
    return process.quantity > 0 ? Math.round((totalReceived / process.quantity) * 100) : 0;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !workOrder) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-lg text-muted-foreground">{error || 'Work order not found'}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const status = STATUS_CONFIG[workOrder.status] || STATUS_CONFIG.created;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{workOrder.work_order_number}</h1>
                <Badge className={`${status.bg} ${status.text}`}>{status.label}</Badge>
              </div>
              <p className="text-muted-foreground text-sm">Work Order (Read-Only View)</p>
            </div>
          </div>
        </div>

        {/* Work Order Details Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Factory className="w-5 h-5" />
              Work Order Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Package className="w-4 h-4" /> Item
                </p>
                <p className="font-semibold">{workOrder.item_name}</p>
                {workOrder.item_code && (
                  <p className="text-xs text-muted-foreground">{workOrder.item_code}</p>
                )}
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Quantity</p>
                <p className="font-semibold text-lg">{workOrder.quantity} {workOrder.unit || 'pcs'}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> Delivery Date
                </p>
                <p className="font-semibold">{formatDate(workOrder.delivery_date)}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Priority</p>
                <p className="font-semibold">{workOrder.priority || '-'}</p>
              </div>
            </div>

            {/* Linked Sales Order */}
            {workOrder.so_number && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <FileText className="w-4 h-4" /> Linked Sales Order
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-semibold text-blue-600">{workOrder.so_number}</span>
                  {workOrder.customer_name && (
                    <span className="text-sm text-muted-foreground">({workOrder.customer_name})</span>
                  )}
                </div>
              </div>
            )}

            {/* Created Info */}
            <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
              <p>Created on {formatDate(workOrder.created_at)} by {workOrder.created_by_name || 'System'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Production Processes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Production Processes</CardTitle>
          </CardHeader>
          <CardContent>
            {workOrder.production_processes && workOrder.production_processes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Process</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-center">Progress</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workOrder.production_processes.map((process, idx) => {
                    const procStatus = getProcessStatus(process);
                    const procStatusConfig = PROCESS_STATUS_CONFIG[procStatus];
                    const progress = getProcessProgress(process);
                    const totalIssued = process.jobs?.reduce((sum, j) => sum + (j.issued_qty || 0), 0) || 0;
                    const totalReceived = process.jobs?.reduce((sum, j) => sum + (j.received_qty || 0), 0) || 0;

                    return (
                      <TableRow key={process.id || idx}>
                        <TableCell className="font-medium">{idx + 1}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{process.process_name}</p>
                            {process.jobs && process.jobs.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {process.jobs.length} job card{process.jobs.length > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{process.quantity}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={progress} className="h-2 flex-1" />
                            <span className="text-xs text-muted-foreground w-10 text-right">{progress}%</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Issued: {totalIssued} | Received: {totalReceived}
                          </p>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={`${procStatusConfig.bg} ${procStatusConfig.text}`}>
                            {procStatusConfig.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No production processes defined</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Read-Only View</p>
            <p className="mt-1">
              This is a read-only view of the work order. To make changes, access the full 
              Work Order page from the Production menu.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WOViewOnlyPage;
